import { lazy, Suspense, useRef, useState, useEffect, useCallback } from "react";
import InlineCouponStrip from "@/components/InlineCouponStrip";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import { ArrowRight, Bot, Zap, Shield, TrendingUp, BarChart3, Settings, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import Layout from "@/components/Layout";
import { useCurrency } from "@/hooks/useCurrency";
const heroImg = "/images/hero-trading.jpg";
import hero3d1 from "@/assets/hero-3d-1.jpg";
import hero3d2 from "@/assets/hero-3d-2.jpg";
import hero3d3 from "@/assets/hero-3d-3.jpg";

const heroImages = [
  { src: hero3d1, alt: "3D Gold Bars with Flowing Light Trails" },
  { src: hero3d2, alt: "Futuristic Trading Dashboard Hologram" },
  { src: hero3d3, alt: "AI Robot Hand Holding Gold Coin" },
];
import goldAbstract from "@/assets/gold-trading-abstract-sm.webp";
import mt5Preview from "@/assets/mt5-preview-sm.jpg";
import bgFeatures from "@/assets/bg-features-sm.webp";
import bgHowItWorks from "@/assets/bg-how-it-works.jpg";
import { useRazorpay } from "@/hooks/useRazorpay";

// Lazy load below-fold components to reduce initial JS and improve TTI
const GoldMarketWidget = lazy(() => import("@/components/GoldMarketWidget"));
const BlogSlider = lazy(() => import("@/components/BlogSlider"));
const TestimonialsSection = lazy(() => import("@/components/TestimonialsSection"));
const InfluencerSection = lazy(() => import("@/components/InfluencerSection"));
const CTASection = lazy(() => import("@/components/CTASection"));

const VisitorCounter = lazy(() => import("@/components/VisitorCounter"));
const MT5Credentials = lazy(() => import("@/components/MT5Credentials"));
const PartnersSection = lazy(() => import("@/components/PartnersSection"));

// Razorpay replaces Superprofile checkout
const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (i: number) => ({
    opacity: 1, y: 0,
    transition: { delay: i * 0.12, duration: 0.7 },
  }),
};

const scaleIn = {
  hidden: { opacity: 0, scale: 0.9 },
  visible: (i: number) => ({
    opacity: 1, scale: 1,
    transition: { delay: i * 0.1, duration: 0.6 },
  }),
};

const stats = [
  { label: "Fully Automated", value: "100%", icon: "⚡" },
  { label: "Platform", value: "MT5", icon: "🖥️" },
  { label: "Market", value: "XAUUSD", icon: "🥇" },
  { label: "Strategy", value: "Scalping", icon: "📊" },
];

const features = [
  { icon: Bot, title: "Fully Automated Execution", desc: "Set it up once — the algorithm monitors the market, places orders, and manages positions around the clock.", gradient: "from-primary/20 to-primary/5" },
  { icon: Zap, title: "Scalping Strategy", desc: "Designed for Gold's volatile intraday price movements with rule-based entry and exit logic.", gradient: "from-accent/20 to-accent/5" },
  { icon: Shield, title: "Built-In Risk Controls", desc: "Includes drawdown limits, dynamic position sizing, and stop-loss placement to help manage exposure.", gradient: "from-primary/20 to-accent/5" },
  { icon: TrendingUp, title: "Gold (XAUUSD) Focused", desc: "Purpose-built exclusively for the Gold market with parameters tuned for XAUUSD characteristics.", gradient: "from-accent/20 to-primary/5" },
  { icon: BarChart3, title: "Performance Monitoring", desc: "Track your equity curve and trade history in real time directly from MetaTrader 5.", gradient: "from-primary/15 to-primary/5" },
  { icon: Settings, title: "MT5 Native", desc: "Runs natively on MetaTrader 5 with zero external plugins or dependencies required.", gradient: "from-accent/15 to-accent/5" },
];

const steps = [
  { step: "01", title: "Sign Up", desc: "Create your account and choose a plan to get started.", color: "primary" as const },
  { step: "02", title: "Install EA", desc: "Download RapidX and install it on your MT5 terminal in minutes.", color: "accent" as const },
  { step: "03", title: "Activate & Run", desc: "Activate your license and let the system execute trades based on its rule-based strategy.", color: "primary" as const },
];

const Index = () => {
  const { initiatePayment } = useRazorpay();
  const { formatPrice } = useCurrency();
  const demoPrice = formatPrice(249);
  const heroRef = useRef<HTMLElement>(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const heroY = useTransform(scrollYProgress, [0, 1], [0, 150]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  const [heroIdx, setHeroIdx] = useState(0);
  useEffect(() => {
    const timer = setInterval(() => setHeroIdx(i => (i + 1) % heroImages.length), 3000);
    return () => clearInterval(timer);
  }, []);

  return (
    <Layout>
      <Helmet>
        <title>NivexA FX — Rule-Based Automated Gold Trading System for MT5</title>
        <meta name="description" content="NivexA RapidX is a rule-based automated trading system for Gold (XAUUSD) scalping on MetaTrader 5." />
        <link rel="canonical" href="https://nivexafx.com/" />
        {/* Hero image preloaded in index.html for LCP discovery */}
        <meta property="og:title" content="NivexA FX — Rule-Based Automated Gold Trading System" />
        <meta property="og:description" content="Automated Expert Advisor for Gold scalping on MT5. Rule-based strategy with built-in risk controls." />
        <meta property="og:url" content="https://nivexafx.com/" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://storage.googleapis.com/gpt-engineer-file-uploads/0PaJwYg0Wqh2UBOiW5M4yR7ptHl2/social-images/social-1772050884569-nivexa_logo.webp" />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>

      {/* Hero */}
      <section ref={heroRef} className="hero-gradient relative min-h-[92vh] flex items-center overflow-hidden">
        {/* Parallax background */}
        <motion.div style={{ y: heroY }} className="absolute inset-0">
          <img src={heroImg} alt="XAUUSD Trading Dashboard" loading="eager" width={1668} height={1080} fetchPriority="high" className="w-full h-full object-cover opacity-20 scale-110" />
          <div className="absolute inset-0 bg-gradient-to-r from-background via-background/85 to-background/40" />
        </motion.div>

        {/* Animated orbs */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-1/4 right-1/4 w-72 h-72 bg-primary/10 rounded-full blur-3xl" style={{ animation: 'float-slow 8s ease-in-out infinite' }} />
          <div className="absolute bottom-1/3 left-1/4 w-96 h-96 bg-accent/8 rounded-full blur-3xl" style={{ animation: 'float-slow 12s ease-in-out infinite 2s' }} />
          <div className="absolute top-1/2 right-1/3 w-48 h-48 bg-primary/5 rounded-full blur-2xl" style={{ animation: 'float-slow 10s ease-in-out infinite 4s' }} />
        </div>

        <motion.div style={{ opacity: heroOpacity }} className="container mx-auto px-4 relative">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div initial="hidden" animate="visible" className="max-w-3xl">
              <motion.div custom={0} variants={fadeUp} className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-accent/30 bg-accent/5 text-accent text-xs font-medium mb-8 backdrop-blur-sm">
                <span className="h-1.5 w-1.5 rounded-full bg-accent animate-pulse" />
                Gold (XAUUSD) Scalping EA for MT5
                <Sparkles className="h-3 w-3" />
              </motion.div>

              <motion.h1 custom={1} variants={fadeUp} className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold leading-[1.1] mb-8">
                Rule-Based{" "}
                <span className="text-gradient-cyan relative">
                  Trading Automation
                  <motion.span
                    className="absolute -bottom-2 left-0 h-1 bg-gradient-to-r from-primary to-primary/0 rounded-full"
                    initial={{ width: 0 }}
                    animate={{ width: "100%" }}
                    transition={{ delay: 0.8, duration: 1, ease: "easeOut" }}
                  />
                </span>
              </motion.h1>

              <motion.p custom={2} variants={fadeUp} className="text-lg md:text-xl text-muted-foreground max-w-xl mb-10 leading-relaxed">
                NivexA RapidX is a fully automated Expert Advisor that uses rule-based logic to execute Gold scalping strategies on MT5 — no manual trading required.
              </motion.p>

              <motion.div custom={3} variants={fadeUp} className="flex flex-wrap gap-4">
                <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 glow-cyan-lg gap-2 text-base h-13 px-10 rounded-xl font-semibold relative overflow-hidden group">
                  <Link to="/pricing">
                    <span className="absolute inset-0 bg-gradient-to-r from-transparent via-foreground/10 to-transparent translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-700" />
                    Try {demoPrice} Demo <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </Button>
                <Button asChild variant="outline" size="lg" className="border-border/50 hover:bg-secondary/50 hover:border-primary/30 text-base h-13 px-10 rounded-xl font-semibold backdrop-blur-sm">
                  <Link to="/product">Explore RapidX Product Details</Link>
                </Button>
              </motion.div>
            </motion.div>

            {/* 3D Hero Image Carousel */}
            <motion.div
              initial={{ opacity: 0, scale: 0.85, x: 60 }}
              animate={{ opacity: 1, scale: 1, x: 0 }}
              transition={{ delay: 0.4, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
              className="hidden lg:block relative"
            >
              <div className="absolute -inset-8 bg-gradient-to-r from-primary/15 to-accent/15 rounded-[50%] blur-3xl opacity-40 animate-pulse" />
              <div className="relative w-full max-w-[520px] mx-auto aspect-square">
                <AnimatePresence mode="wait">
                  <motion.img
                    key={heroIdx}
                    src={heroImages[heroIdx].src}
                    alt={heroImages[heroIdx].alt}
                    width={1024}
                    height={1024}
                    loading="eager"
                    fetchPriority="high"
                    className="absolute inset-0 w-full h-full object-cover rounded-[2rem] drop-shadow-[0_0_60px_rgba(0,229,255,0.25)]"
                    initial={{ opacity: 0, scale: 0.92, rotateY: 15 }}
                    animate={{ opacity: 1, scale: 1, rotateY: 0 }}
                    exit={{ opacity: 0, scale: 0.92, rotateY: -15 }}
                    transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                  />
                </AnimatePresence>
                {/* Dots indicator */}
                <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 flex gap-2">
                  {heroImages.map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setHeroIdx(i)}
                      className={`w-2 h-2 rounded-full transition-all duration-300 ${i === heroIdx ? 'bg-primary w-6' : 'bg-muted-foreground/30 hover:bg-muted-foreground/50'}`}
                    />
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </motion.div>

        {/* Bottom fade */}
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-background to-transparent pointer-events-none" />
      </section>

      {/* Stats Bar */}
      <section className="relative -mt-8 z-10">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="glass-strong rounded-2xl p-6 md:p-8"
          >
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {stats.map((s, i) => (
                <motion.div
                  key={s.label}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="text-center group"
                >
                  <div className="text-2xl mb-2">{s.icon}</div>
                  <div className="text-2xl md:text-3xl font-display font-bold text-gradient-cyan">{s.value}</div>
                  <div className="text-xs text-muted-foreground mt-1 uppercase tracking-wider">{s.label}</div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Live Gold Market Widget */}
      <Suspense fallback={<div className="py-20" />}>
        <GoldMarketWidget />
      </Suspense>

      {/* What is RapidX */}
      <section className="py-28 relative overflow-hidden">
        <img src={bgFeatures} alt="" className="absolute inset-0 w-full h-full object-cover opacity-15 pointer-events-none" aria-hidden="true" loading="lazy" />
        <div className="absolute inset-0 bg-gradient-to-b from-background via-background/60 to-background pointer-events-none" />
        
        {/* Decorative orbs */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 right-10 w-64 h-64 bg-primary/8 rounded-full blur-3xl" style={{ animation: 'float-slow 10s ease-in-out infinite' }} />
          <div className="absolute bottom-20 left-10 w-80 h-80 bg-accent/6 rounded-full blur-3xl" style={{ animation: 'float-slow 14s ease-in-out infinite 3s' }} />
        </div>

        <div className="container mx-auto px-4 relative">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-20">
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
            >
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-accent/20 bg-accent/5 text-accent text-xs font-medium mb-6">
                About the Product
              </span>
              <h2 className="font-display text-3xl md:text-5xl font-bold mb-6 leading-tight">
                What is <span className="text-gradient-gold">NivexA RapidX</span>?
              </h2>
              <p className="text-muted-foreground leading-relaxed text-lg">
                A rule-based Expert Advisor that automates Gold scalping with systematic execution, built-in risk controls, and real-time market monitoring on MetaTrader 5.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="relative group"
            >
              <div className="absolute -inset-4 bg-gradient-to-r from-primary/20 to-accent/20 rounded-2xl blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
              <div className="relative glass-strong rounded-2xl overflow-hidden">
                <img src={goldAbstract} alt="Gold Trading Visualization" loading="lazy" width={1024} height={477} className="w-full h-72 object-cover group-hover:scale-105 transition-transform duration-700" />
              </div>
            </motion.div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((f, i) => (
              <motion.div
                key={f.title}
                custom={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={scaleIn}
                className="glass rounded-2xl p-7 card-hover-glow group relative overflow-hidden"
              >
                {/* Gradient background on hover */}
                <div className={`absolute inset-0 bg-gradient-to-br ${f.gradient} opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />
                
                <div className="relative">
                  <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center mb-5 group-hover:glow-cyan transition-all duration-500 group-hover:scale-110">
                    <f.icon className="h-6 w-6 text-primary" />
                  </div>
                  <h3 className="font-display font-semibold text-lg mb-3">{f.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{f.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-28 relative overflow-hidden">
        <img src={bgHowItWorks} alt="" width={1920} height={902} className="absolute inset-0 w-full h-full object-cover opacity-10 pointer-events-none" aria-hidden="true" loading="lazy" />
        <div className="absolute inset-0 bg-gradient-to-b from-background via-background/70 to-background pointer-events-none" />
        
        <div className="container mx-auto px-4 relative">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-primary/20 bg-primary/5 text-primary text-xs font-medium mb-6">
              Getting Started
            </span>
            <h2 className="font-display text-3xl md:text-5xl font-bold mb-4">
              How It <span className="text-gradient-cyan">Works</span>
            </h2>
            <p className="text-muted-foreground text-lg">Get started in three simple steps.</p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto relative">
            {/* Connecting line */}
            <div className="hidden md:block absolute top-16 left-[20%] right-[20%] h-px bg-gradient-to-r from-primary/30 via-accent/30 to-primary/30" />
            
            {steps.map((s, i) => (
              <motion.div
                key={s.step}
                custom={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
                className="text-center relative"
              >
                <div className="relative inline-flex mb-6">
                  <div className={`h-16 w-16 rounded-2xl bg-${s.color}/10 border border-${s.color}/20 flex items-center justify-center mx-auto relative`}>
                    <span className={`font-display text-2xl font-bold text-${s.color}`}>{s.step}</span>
                  </div>
                  {/* Pulse ring */}
                  <div className={`absolute inset-0 rounded-2xl border border-${s.color}/30`} style={{ animation: 'pulse-ring 2.5s ease-out infinite', animationDelay: `${i * 0.5}s` }} />
                </div>
                <h3 className="font-display font-semibold text-xl mb-3">{s.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed max-w-xs mx-auto">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* MT5 Preview */}
      <section className="py-28 section-gradient relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary/5 rounded-full blur-3xl" style={{ animation: 'float-slow 12s ease-in-out infinite' }} />
        </div>

        <div className="container mx-auto px-4 relative">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-primary/20 bg-primary/5 text-primary text-xs font-medium mb-6">
              Results & Data
            </span>
            <h2 className="font-display text-3xl md:text-5xl font-bold mb-4">
              Historical <span className="text-gradient-cyan">Performance</span>
            </h2>
            <p className="text-muted-foreground text-lg max-w-xl mx-auto">Backtest and historical equity data from test accounts.</p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.95 }}
            whileInView={{ opacity: 1, y: 0, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="relative max-w-4xl mx-auto group"
          >
            <div className="absolute -inset-4 bg-gradient-to-r from-primary/10 via-accent/5 to-primary/10 rounded-3xl blur-2xl opacity-50 group-hover:opacity-100 transition-opacity duration-700" />
            <div className="relative glass-strong rounded-2xl p-2 overflow-hidden">
              <img src={mt5Preview} alt="MT5 XAUUSD Historical Performance Data" loading="lazy" width={1920} height={1080} className="w-full rounded-xl" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Why Choose NivexA */}
      <section className="py-28 relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-1/3 left-1/4 w-80 h-80 bg-primary/8 rounded-full blur-3xl" style={{ animation: 'float-slow 11s ease-in-out infinite' }} />
          <div className="absolute bottom-1/4 right-1/4 w-64 h-64 bg-accent/6 rounded-full blur-3xl" style={{ animation: 'float-slow 9s ease-in-out infinite 2s' }} />
        </div>
        <div className="container mx-auto px-4 relative">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-16">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-accent/20 bg-accent/5 text-accent text-xs font-medium mb-6">
              Why NivexA
            </span>
            <h2 className="font-display text-3xl md:text-5xl font-bold mb-4">
              Why Traders <span className="text-gradient-gold">Trust Us</span>
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">Built by traders, for traders — NivexA RapidX is engineered with precision, transparency, and performance at its core.</p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {[
              { emoji: "🔒", title: "Transparent Strategy", desc: "No black-box magic. RapidX uses clearly defined, rule-based logic so you know exactly how it trades — entry, exit, and risk management rules are fully documented." },
              { emoji: "⚙️", title: "Zero Dependencies", desc: "Runs natively on MetaTrader 5 without any external plugins, VPS requirements, or third-party services. Just install, activate, and trade." },
              { emoji: "📈", title: "Backtested & Validated", desc: "Every strategy update goes through rigorous backtesting across multiple market conditions — including high-volatility events and low-liquidity periods." },
              { emoji: "🛡️", title: "Risk-First Design", desc: "Built-in drawdown limits, dynamic position sizing, and smart stop-loss placement ensure your capital is protected even during adverse market conditions." },
              { emoji: "🌍", title: "Global Community", desc: "Join thousands of traders across 30+ countries who rely on NivexA for consistent, automated Gold trading on MT5." },
              { emoji: "🎯", title: "Dedicated Support", desc: "Get access to our dedicated support team via email. We're here to help with installation, configuration, and optimization." },
            ].map((item, i) => (
              <motion.div
                key={item.title}
                custom={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
                className="glass rounded-2xl p-7 card-hover-glow group flex gap-5"
              >
                <div className="text-3xl flex-shrink-0 mt-1">{item.emoji}</div>
                <div>
                  <h3 className="font-display font-semibold text-lg mb-2">{item.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Security & Trust */}
      <section className="py-24 section-gradient relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-0 right-1/3 w-72 h-72 bg-primary/5 rounded-full blur-3xl" style={{ animation: 'float-slow 10s ease-in-out infinite' }} />
        </div>
        <div className="container mx-auto px-4 relative">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-14">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-primary/20 bg-primary/5 text-primary text-xs font-medium mb-6">
              Security & Trust
            </span>
            <h2 className="font-display text-3xl md:text-5xl font-bold mb-4">
              Your Capital, <span className="text-gradient-cyan">Our Priority</span>
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">We take security seriously. Every aspect of NivexA is built with trader safety in mind.</p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
            {[
              { icon: "🔐", label: "Licensed EA", sub: "Unique license key per user" },
              { icon: "📊", label: "Real-Time Monitoring", sub: "Track equity & trades live" },
              { icon: "🚫", label: "No Fund Access", sub: "EA runs on your broker only" },
              { icon: "✅", label: "Verified Payments", sub: "Secure Razorpay gateway" },
            ].map((item, i) => (
              <motion.div
                key={item.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="glass rounded-2xl p-6 text-center card-hover-glow"
              >
                <div className="text-3xl mb-3">{item.icon}</div>
                <h3 className="font-display font-semibold text-base mb-1">{item.label}</h3>
                <p className="text-xs text-muted-foreground">{item.sub}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* MT5 Live Credentials */}
      <Suspense fallback={<div className="py-20" />}>
        <MT5Credentials />
      </Suspense>

      {/* Partners */}
      <Suspense fallback={<div className="py-20" />}>
        <PartnersSection />
      </Suspense>

      {/* Blog Slider */}
      <Suspense fallback={<div className="py-20" />}>
        <BlogSlider />
      </Suspense>

      {/* Testimonials */}
      <Suspense fallback={<div className="py-20" />}>
        <TestimonialsSection />
      </Suspense>

      {/* Influencer Partnership */}
      <Suspense fallback={<div className="py-20" />}>
        <InfluencerSection />
      </Suspense>

      {/* Visitor Counter */}
      <section className="py-8">
        <div className="container mx-auto px-4 text-center">
          <Suspense fallback={<div className="py-4" />}>
            <VisitorCounter />
          </Suspense>
        </div>
      </section>

      {/* Tagline + Coupon */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary/5 rounded-full blur-3xl" />
        </div>
        <div className="container mx-auto px-4 text-center relative space-y-6">
          <motion.p
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="font-display text-2xl md:text-4xl font-light text-muted-foreground/80 italic max-w-2xl mx-auto leading-relaxed"
          >
            "Intelligent Trading Automation Built for Modern Markets"
          </motion.p>
          <InlineCouponStrip />
        </div>
      </section>


      {/* CTA */}
      <Suspense fallback={<div className="py-20" />}>
        <CTASection onCtaClick={() => window.location.href = "/pricing"} />
      </Suspense>
    </Layout>
  );
};

export default Index;
